import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Dialog, Input, PriceInput, Select } from '@artosera/ui';
import { TYPES_POLICE, DEVISES } from '@artosera/shared';
import {
  messageErreurAssurance,
  useEnregistrerPolice,
  useArchiverPolice,
  type PolicePresentee,
  type CorpsPolice,
} from '../../api/assurance.js';
import { useContact, type ContactPresente } from '../../api/contacts.js';
import { useLieux } from '../../api/lieux.js';
import { estApiError } from '../../api/errors.js';
import { RechercheContact } from '../ventes/RechercheContact.js';
import { RechercheOeuvre } from '../ventes/RechercheOeuvre.js';
import { Field } from '../Field.js';
import styles from './AssurancePage.module.css';

export function PoliceDialog({
  galerieSlug,
  police,
  onFermer,
  onRecharger,
}: {
  galerieSlug: string;
  police: PolicePresentee | null;
  onFermer: () => void;
  onRecharger: () => void;
}) {
  const { t, i18n } = useTranslation(['regie', 'commun']);
  const enregistrer = useEnregistrerPolice(galerieSlug);
  const archiver = useArchiverPolice(galerieSlug);
  const initial = useContact(galerieSlug, police?.assureurContactId ?? null);
  const lieux = useLieux(galerieSlug);
  const [contact, setContact] = useState<ContactPresente | null>(null);
  const [contactChange, setContactChange] = useState(false);
  const [numero, setNumero] = useState(police?.numero ?? '');
  const [type, setType] = useState<CorpsPolice['type']>(police?.type ?? 'stock');
  const [devise, setDevise] = useState<CorpsPolice['devise']>(police?.devise ?? 'EUR');
  const [debut, setDebut] = useState(police?.debut.slice(0, 10) ?? '');
  const [fin, setFin] = useState(police?.fin.slice(0, 10) ?? '');
  const [plafond, setPlafond] = useState<number | null>(police?.plafondGlobalCentimes ?? null);
  const [parOeuvre, setParOeuvre] = useState<number | null>(
    police?.plafondParOeuvreCentimes ?? null,
  );
  const [franchise, setFranchise] = useState<number | null>(police?.franchiseCentimes ?? null);
  const [lieuIds, setLieuIds] = useState(police?.lieuIds ?? []);
  const [oeuvres, setOeuvres] = useState(
    (police?.oeuvreIds ?? []).map((id) => ({ id, titre: id })),
  );
  const [erreur, setErreur] = useState('');
  const [conflit, setConflit] = useState(false);
  const assureur = contactChange ? contact : (initial.data?.contact ?? contact);
  function echec(e: unknown) {
    setErreur(messageErreurAssurance(e));
    setConflit(estApiError(e) && e.status === 409);
  }
  async function soumettre() {
    setErreur('');
    try {
      const corps: CorpsPolice = {
        assureurContactId: assureur?.id ?? police?.assureurContactId ?? '',
        numero,
        type,
        devise,
        debut: debut === police?.debut.slice(0, 10) ? police.debut : new Date(debut).toISOString(),
        fin: fin === police?.fin.slice(0, 10) ? police.fin : new Date(fin).toISOString(),
        lieuIds,
        oeuvreIds: oeuvres.map((o) => o.id),
        archivee: police?.archivee ?? false,
        ...(plafond === null ? {} : { plafondGlobalCentimes: plafond }),
        ...(parOeuvre === null ? {} : { plafondParOeuvreCentimes: parOeuvre }),
        ...(franchise === null ? {} : { franchiseCentimes: franchise }),
        ...(police?.mediaId ? { mediaId: police.mediaId } : {}),
        ...(police?.documentId ? { documentId: police.documentId } : {}),
      };
      await enregistrer.mutateAsync({
        ...(police ? { id: police._id, revision: police.revision } : {}),
        corps,
      });
      onFermer();
    } catch (e) {
      echec(e);
    }
  }
  return (
    <Dialog
      open
      onOpenChange={(v) => {
        if (!v) onFermer();
      }}
      title={t(police ? 'regie:assurance.modifierPolice' : 'regie:assurance.creerPolice')}
      closeLabel={t('commun:actions.fermer')}
    >
      <form
        className={styles.form}
        onSubmit={(e) => {
          e.preventDefault();
          void soumettre();
        }}
      >
        <fieldset>
          <legend>{t('regie:assurance.assureur')}</legend>
          <RechercheContact
            galerieSlug={galerieSlug}
            contact={assureur}
            onChoisir={(c) => {
              setContact(c);
              setContactChange(true);
            }}
            onEffacer={() => {
              setContact(null);
              setContactChange(true);
            }}
          />
        </fieldset>
        <Field htmlFor="police-numero" label={t('regie:assurance.numero')}>
          <Input
            id="police-numero"
            required
            maxLength={120}
            value={numero}
            onChange={(e) => setNumero(e.target.value)}
          />
        </Field>
        <Field htmlFor="police-type" label={t('regie:assurance.typePolice')}>
          <Select
            id="police-type"
            value={type}
            onValueChange={(v) => setType(v as CorpsPolice['type'])}
            options={TYPES_POLICE.map((value) => ({
              value,
              label: t(`regie:assurance.typesPolice.${value}`),
            }))}
          />
        </Field>
        <Field htmlFor="police-devise" label={t('regie:assurance.devise')}>
          <Select
            id="police-devise"
            value={devise}
            onValueChange={(v) => setDevise(v as CorpsPolice['devise'])}
            options={DEVISES.map((value) => ({ value, label: value }))}
          />
        </Field>
        <Field htmlFor="police-debut" label={t('regie:assurance.debut')}>
          <Input
            id="police-debut"
            type="date"
            required
            value={debut}
            onChange={(e) => setDebut(e.target.value)}
          />
        </Field>
        <Field htmlFor="police-fin" label={t('regie:assurance.fin')}>
          <Input
            id="police-fin"
            type="date"
            required
            min={debut}
            value={fin}
            onChange={(e) => setFin(e.target.value)}
          />
        </Field>
        {[
          { cle: 'plafondGlobal', valeur: plafond, changer: setPlafond },
          { cle: 'plafondOeuvre', valeur: parOeuvre, changer: setParOeuvre },
          { cle: 'franchise', valeur: franchise, changer: setFranchise },
        ].map((champ) => (
          <Field key={champ.cle} htmlFor={champ.cle} label={t(`regie:assurance.${champ.cle}`)}>
            <PriceInput
              optional
              id={champ.cle}
              currency={devise}
              lang={i18n.language === 'en' ? 'en' : 'fr'}
              cents={champ.valeur}
              onCentsChange={champ.changer}
            />
          </Field>
        ))}
        {police && <p>{t('regie:assurance.montantsConserves')}</p>}
        <fieldset>
          <legend>{t('regie:assurance.lieux')}</legend>
          {lieux.data?.map((l) => (
            <label key={l.id} className={styles.actions}>
              <input
                type="checkbox"
                checked={lieuIds.includes(l.id)}
                onChange={(e) =>
                  setLieuIds(
                    e.target.checked ? [...lieuIds, l.id] : lieuIds.filter((id) => id !== l.id),
                  )
                }
              />
              {l.nom}
            </label>
          ))}
        </fieldset>
        <fieldset>
          <legend>{t('regie:assurance.oeuvres')}</legend>
          {oeuvres.map((o) => (
            <div key={o.id} className={styles.actions}>
              {o.titre}
              <Button
                type="button"
                variant="ghost"
                onClick={() => setOeuvres(oeuvres.filter((x) => x.id !== o.id))}
              >
                {t('regie:assurance.retirer')}
              </Button>
            </div>
          ))}
          <RechercheOeuvre
            galerieSlug={galerieSlug}
            onChoisir={(o) => {
              if (!oeuvres.some((x) => x.id === o.id))
                setOeuvres([...oeuvres, { id: o.id, titre: o.titre }]);
            }}
          />
        </fieldset>
        {erreur && <p role="alert">{erreur}</p>}
        {conflit && (
          <Button type="button" onClick={onRecharger}>
            {t('regie:assurance.recharger')}
          </Button>
        )}
        <Button
          type="submit"
          loading={enregistrer.isPending}
          disabled={
            conflit || (!assureur && (!police || contactChange)) || !debut || !fin || fin <= debut
          }
        >
          {t('commun:actions.enregistrer')}
        </Button>
        {police && !police.archivee && (
          <Button
            type="button"
            variant="danger"
            disabled={conflit}
            loading={archiver.isPending}
            onClick={() => {
              void archiver.mutateAsync(police).then(onFermer).catch(echec);
            }}
          >
            {t('regie:assurance.archiverPolice')}
          </Button>
        )}
      </form>
    </Dialog>
  );
}
